import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Typography,
  Button,
  Space,
  Select,
  Tag,
  Row,
  Col,
  Card,
  Switch,
  Tooltip,
} from "antd";
import {
  ReloadOutlined,
  HeartOutlined,
  DesktopOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  InboxOutlined,
  MoonOutlined,
  ApiOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getFleetHealth } from "../network/api";
import PageHeader from "./common/PageHeader";
import StatCard from "./common/StatCard";
import InfoHint from "./common/InfoHint";
import DataTable from "./common/DataTable";
import FleetMap from "./FleetMap";
import { notify, serverError } from "../utils/notify";
import { TRAFFIC } from "../theme/themeConfig";
import {
  HEALTH_STATE_LABEL,
  HEALTH_STATE_TAG_COLOR,
  HEALTH_DOT_COLOR,
  HEALTH_STATE_COLOR,
  HEALTH_COLOR_RANK,
  HEALTH_STATE_OPTIONS,
  HEALTH_COLOR_OPTIONS,
  PROBLEM_STATE_OPTIONS,
  HEALTH_STATES,
  LIFECYCLE_LABEL,
  LIFECYCLE_TAG_COLOR,
  LIFECYCLE_OPTIONS,
  relativeTime,
} from "../constants/health";

const { Text } = Typography;

const AUTO_REFRESH_MS = 30000;

// Small coloured status dot used in the table cell.
const StatusDot = ({ color }) => (
  <span
    style={{
      display: "inline-block",
      width: 10,
      height: 10,
      borderRadius: "50%",
      marginRight: 8,
      backgroundColor: HEALTH_DOT_COLOR[color] || "#d9d9d9",
    }}
  />
);

const FleetHealth = () => {
  const navigate = useNavigate();

  const [devices, setDevices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  // Server-side filters wired to the query params (search is handled client-side
  // by DataTable since the list is small/already loaded).
  const [stateFilter, setStateFilter] = useState();
  const [colorFilter, setColorFilter] = useState();
  const [lifecycleFilter, setLifecycleFilter] = useState();
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [imageAge, setImageAge] = useState(); // undefined | "le24" | "gt24" | "gt48"
  // Region is filtered client-side (it lives on the outlet, not the health row) so
  // one loaded fleet can be sliced by territory without another round-trip.
  const [regionFilter, setRegionFilter] = useState();
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getFleetHealth({
        state: stateFilter,
        color: colorFilter,
        lifecycle: lifecycleFilter,
        remoteFixable: remoteOnly ? true : undefined,
      });
      setSummary(response.summary || null);
      const formatted = (response.devices || []).map((d) => ({
        key: d.deviceId,
        deviceId: d.deviceId,
        deviceTitle: d.deviceTitle,
        state: d.state,
        color: d.color || HEALTH_STATE_COLOR[d.state],
        reasons: d.reasons || [],
        since: d.since,
        lastStatsAt: d.lastStatsAt,
        lastPictureAt: d.lastPictureAt,
        lifecycle: d.lifecycle,
        remoteAccess: d.remoteAccess || null,
        agentVersionLabel: d.agentVersionLabel,
        tags: d.tags || [],
        outlet: d.outlet || null,
      }));
      setDevices(formatted);
    } catch (error) {
      console.error("Error fetching fleet health:", error);
      notify.error(serverError(error, "Failed to load fleet health."));
    } finally {
      setLoading(false);
    }
  }, [stateFilter, colorFilter, lifecycleFilter, remoteOnly]);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  // Auto-refresh on a clearable interval.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(fetchHealth, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, fetchHealth]);

  const openDevice = (deviceId) => {
    navigate(`/dashboard/health/${deviceId}`);
  };

  // `summary` reports the DEPLOYED fleet. Bench units and shut shops live in
  // `excluded` and are shown as their own neutral tiles — they are inventory and
  // opening hours, not faults, and counting them as red is what made the old board
  // unusable (42/50 red, of which 24 were neither).
  const byColor = summary?.byColor || {};
  const byState = summary?.byState || {};
  const excluded = summary?.excluded || {};
  const notDeployed = excluded[HEALTH_STATES.NOT_DEPLOYED] ?? 0;
  const offHours = excluded[HEALTH_STATES.OFF_HOURS] ?? 0;
  const remoteFixable = summary?.remoteFixable ?? 0;
  const fleetTotal = summary?.fleet?.total ?? 0;

  // Distinct territories present in the loaded fleet, for the region dropdown.
  const regionOptions = useMemo(
    () =>
      [...new Set(devices.map((d) => d.outlet?.region).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b))
        .map((r) => ({ label: r, value: r })),
    [devices]
  );

  // Region is applied first: every downstream count (image-age buttons, map,
  // freshness buckets) is scoped to the selected territory so the page drills down
  // by region without touching the server-side state/color/lifecycle filters.
  const regionDevices = regionFilter
    ? devices.filter((d) => d.outlet?.region === regionFilter)
    : devices;

  // Last-image freshness buckets, computed over the currently loaded devices so the
  // counts track whatever server-side filters (state/color/lifecycle) are applied.
  // >24h and >48h are cumulative (everything older than the threshold, incl. never).
  const HOUR = 3600 * 1000;
  const nowMs = Date.now();
  const imageAgeMs = (d) =>
    d.lastPictureAt ? nowMs - new Date(d.lastPictureAt).getTime() : Infinity;
  const imgCounts = { le24: 0, gt24: 0, gt48: 0 };
  regionDevices.forEach((d) => {
    const a = imageAgeMs(d);
    if (a <= 24 * HOUR) imgCounts.le24 += 1;
    else imgCounts.gt24 += 1;
    if (a > 48 * HOUR) imgCounts.gt48 += 1;
  });
  const matchesImageAge = (d) => {
    if (!imageAge) return true;
    const a = imageAgeMs(d);
    if (imageAge === "le24") return a <= 24 * HOUR;
    if (imageAge === "gt24") return a > 24 * HOUR;
    if (imageAge === "gt48") return a > 48 * HOUR;
    return true;
  };
  const visibleDevices = regionDevices.filter(matchesImageAge);
  const geoDevices = regionDevices.filter((d) => d.outlet?.lat != null && d.outlet?.lng != null);

  const columns = [
    {
      title: "Device ID",
      dataIndex: "deviceId",
      key: "deviceId",
      sorter: (a, b) => (a.deviceId || "").localeCompare(b.deviceId || ""),
    },
    {
      title: "Title",
      dataIndex: "deviceTitle",
      key: "deviceTitle",
      sorter: (a, b) =>
        (a.deviceTitle || "").localeCompare(b.deviceTitle || ""),
    },
    {
      title: <span>Distribution<InfoHint title="The outlet this device sits in and its distribution owners: territory (region › zone), the ASM supervisor, and the merchandiser. Synced from the outlet source." /></span>,
      key: "distribution",
      render: (_, record) => {
        const o = record.outlet;
        if (!o || !o.title) return <Text type="secondary">Unmapped</Text>;
        const territory = [o.region, o.zone].filter(Boolean).join(" › ");
        return (
          <div style={{ lineHeight: 1.35 }}>
            <div>
              <Text strong style={{ fontSize: 13 }}>{o.title}</Text>
              {o.code && <Text type="secondary" style={{ fontSize: 12 }}> ({o.code})</Text>}
            </div>
            {territory && <div><Text type="secondary" style={{ fontSize: 12 }}>{territory}</Text></div>}
            {o.supervisor && (
              <div><Text type="secondary" style={{ fontSize: 12 }}>Supervisor: {o.supervisor}</Text></div>
            )}
            {o.merchandiser && (
              <div><Text type="secondary" style={{ fontSize: 12 }}>MR: {o.merchandiser}</Text></div>
            )}
            {o.distributor && (
              <div><Text type="secondary" style={{ fontSize: 12 }}>Distributor: {o.distributor}</Text></div>
            )}
          </div>
        );
      },
    },
    {
      title: <span>Location<InfoHint title="Opens the outlet's GPS location in Google Maps. Only outlets with coordinates captured in the field have a link." /></span>,
      key: "location",
      render: (_, record) => {
        const o = record.outlet;
        if (o?.mapsUrl) {
          return (
            <a
              href={o.mapsUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <EnvironmentOutlined /> Map
            </a>
          );
        }
        return <Tag color="warning">No GPS</Tag>;
      },
    },
    {
      title: <span>Status<InfoHint title="Overall health of the camera, worst-first: Red = critical/offline, Yellow = warning, Green = healthy." /></span>,
      dataIndex: "state",
      key: "state",
      // Default sort puts red first so the worst devices surface at the top.
      defaultSortOrder: "ascend",
      sorter: (a, b) =>
        (HEALTH_COLOR_RANK[a.color] ?? 99) - (HEALTH_COLOR_RANK[b.color] ?? 99),
      render: (state, record) => (
        <span>
          <StatusDot color={record.color} />
          <Tag color={HEALTH_STATE_TAG_COLOR[state]}>
            {HEALTH_STATE_LABEL[state] || state}
          </Tag>
        </span>
      ),
    },
    {
      title: <span>Lifecycle<InfoHint title="Where the device is in its deployment lifecycle — e.g. in stock, deployed, or retired. Separates real inventory state from health faults." /></span>,
      dataIndex: "lifecycle",
      key: "lifecycle",
      render: (lifecycle) =>
        lifecycle ? (
          <Tag color={LIFECYCLE_TAG_COLOR[lifecycle]}>
            {LIFECYCLE_LABEL[lifecycle] || lifecycle}
          </Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      // The most expensive question in a triage: can this be fixed from a desk, or
      // does somebody have to drive to the shop? Agent v3.2.5+ ships an SSH client.
      title: <span>Fix<InfoHint title="Whether an unhealthy device can be fixed remotely over SSH (agent v3.2.5+) or needs a site visit. Saves a wasted trip." /></span>,
      dataIndex: "remoteAccess",
      key: "remoteAccess",
      render: (remote, record) => {
        if (record.state === HEALTH_STATES.HEALTHY) {
          return <Text type="secondary">—</Text>;
        }
        if (!remote) return <Text type="secondary">—</Text>;
        return (
          <Tooltip title={remote.reason}>
            <Tag color={remote.capable ? "green" : "default"}>
              {remote.capable ? "Remote (SSH)" : "Site visit"}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: <span>Reasons<InfoHint title="Why the device is in its current state — e.g. no heartbeat, no recent images, or a sensor fault." /></span>,
      dataIndex: "reasons",
      key: "reasons",
      render: (reasons) =>
        reasons && reasons.length ? (
          <Text type="secondary">{reasons.join(", ")}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: <span>Since<InfoHint title="How long the device has been in its current health state." /></span>,
      dataIndex: "since",
      key: "since",
      sorter: (a, b) => new Date(a.since || 0) - new Date(b.since || 0),
      render: (since) => relativeTime(since),
    },
    {
      title: <span>Last Heartbeat<InfoHint title="When the device last checked in with the server (status/stats). A stale heartbeat usually means the device is offline." /></span>,
      dataIndex: "lastStatsAt",
      key: "lastStatsAt",
      sorter: (a, b) =>
        new Date(a.lastStatsAt || 0) - new Date(b.lastStatsAt || 0),
      render: (lastStatsAt) => relativeTime(lastStatsAt),
    },
    {
      title: <span>Last Image<InfoHint title="When the device last uploaded a captured photo. Gaps here point to a camera or capture problem." /></span>,
      dataIndex: "lastPictureAt",
      key: "lastPictureAt",
      sorter: (a, b) =>
        new Date(a.lastPictureAt || 0) - new Date(b.lastPictureAt || 0),
      render: (lastPictureAt) => relativeTime(lastPictureAt),
    },
  ];

  const toolbar = (
    <>
      <Select
        showSearch
        placeholder="Filter by region"
        allowClear
        style={{ width: 200 }}
        value={regionFilter}
        options={regionOptions}
        optionFilterProp="label"
        onChange={(value) => setRegionFilter(value)}
      />
      <Select
        placeholder="Filter by state"
        allowClear
        style={{ width: 200 }}
        value={stateFilter}
        options={HEALTH_STATE_OPTIONS}
        onChange={(value) => setStateFilter(value)}
      />
      <Select
        placeholder="Filter by color"
        allowClear
        style={{ width: 160 }}
        value={colorFilter}
        options={HEALTH_COLOR_OPTIONS}
        onChange={(value) => setColorFilter(value)}
      />
      <Select
        placeholder="Filter by lifecycle"
        allowClear
        style={{ width: 180 }}
        value={lifecycleFilter}
        options={LIFECYCLE_OPTIONS}
        onChange={(value) => setLifecycleFilter(value)}
      />
      <Tooltip title="Only devices reachable over SSH right now (agent v3.2.5+ and online)">
        <Button
          type={remoteOnly ? "primary" : "default"}
          icon={<ApiOutlined />}
          onClick={() => setRemoteOnly((v) => !v)}
        >
          Remote-fixable
        </Button>
      </Tooltip>
      <Tooltip title="Filter by how long since the last uploaded image. >24h and >48h are cumulative (everything older than the threshold).">
        <Space.Compact>
          {[
            ["le24", "≤24h", imgCounts.le24],
            ["gt24", ">24h", imgCounts.gt24],
            ["gt48", ">48h", imgCounts.gt48],
          ].map(([val, label, count]) => (
            <Button
              key={val}
              type={imageAge === val ? "primary" : "default"}
              onClick={() => setImageAge((cur) => (cur === val ? undefined : val))}
            >
              {label} ({count})
            </Button>
          ))}
        </Space.Compact>
      </Tooltip>
    </>
  );

  return (
    <div>
      <PageHeader
        title="Fleet Health"
        subtitle="Live status across all cameras"
        tooltip="Live health of every camera in the fleet. The table auto-refreshes; click any row to drill into one device. Red devices need attention first."
        icon={<HeartOutlined />}
        extra={
          <>
            <Text type="secondary">Auto-refresh</Text>
            <Switch
              checked={autoRefresh}
              onChange={setAutoRefresh}
              checkedChildren="On"
              unCheckedChildren="Off"
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchHealth}
              loading={loading}
            >
              Refresh
            </Button>
          </>
        }
      />

      {/* Deployed fleet — the numbers to act on. */}
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={8} md={4}>
          <StatCard
            title="Deployed Fleet"
            value={summary?.total ?? 0}
            tooltip="Every camera registered in the fleet, whatever its health state."
            icon={<DesktopOutlined />}
            accent="#2f54eb"
            footer={
              fleetTotal > (summary?.total ?? 0) ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  of {fleetTotal} registered
                </Text>
              ) : null
            }
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <StatCard
            title="Green"
            value={byColor.green ?? 0}
            tooltip="Healthy cameras — checking in and capturing normally, no active alerts."
            icon={<CheckCircleOutlined />}
            accent={TRAFFIC.green}
            valueStyle={{ color: TRAFFIC.green }}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <StatCard
            title="Yellow"
            value={byColor.yellow ?? 0}
            tooltip="Warning state — something is degraded (e.g. delayed images or heartbeat) but the camera is not fully down."
            icon={<WarningOutlined />}
            accent={TRAFFIC.yellow}
            valueStyle={{ color: TRAFFIC.yellow }}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <StatCard
            title="Red"
            value={byColor.red ?? 0}
            tooltip="Critical state — offline or not capturing. These need attention first."
            icon={<CloseCircleOutlined />}
            accent={TRAFFIC.red}
            valueStyle={{ color: TRAFFIC.red }}
          />
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Card
            title={<span>Deployed fleet by state<InfoHint title="Breakdown of the deployed cameras by detailed health state — a finer split than the red/yellow/green colours. Excludes bench/QC units and off-hours shops, which are shown separately below." /></span>}
            size="small"
            style={{ height: "100%" }}
          >
            <Space wrap>
              {PROBLEM_STATE_OPTIONS.map((opt) => (
                <Tag key={opt.value} color={HEALTH_STATE_TAG_COLOR[opt.value]}>
                  {opt.label}: {byState[opt.value] ?? 0}
                </Tag>
              ))}
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Not in service + what can be fixed without a van. These are deliberately
          kept OUT of the traffic lights above: a device on the bench is inventory
          and a shut shop is opening hours — neither is a fault to triage. */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={12} sm={8} md={6}>
          <StatCard
            title="Not Deployed (QC / bench)"
            value={notDeployed}
            tooltip="Devices not yet in service — in QC or on the bench. They're inventory, not faults, so they're kept out of the red/yellow/green counts above."
            icon={<InboxOutlined />}
            accent={TRAFFIC.grey}
            valueStyle={{ color: TRAFFIC.grey }}
          />
        </Col>
        <Col xs={12} sm={8} md={6}>
          <StatCard
            title="Off Hours (shop shut)"
            value={offHours}
            tooltip="Devices that look quiet only because their shop is closed. Expected downtime, not a fault — excluded from the health counts above."
            icon={<MoonOutlined />}
            accent={TRAFFIC.grey}
            valueStyle={{ color: TRAFFIC.grey }}
          />
        </Col>
        <Col xs={12} sm={8} md={6}>
          <StatCard
            title="Needs Re-image"
            value={byState[HEALTH_STATES.NEEDS_REIMAGE] ?? 0}
            tooltip="Devices whose software is broken enough that they need re-flashing with a fresh image to recover."
            icon={<CloseCircleOutlined />}
            accent={TRAFFIC.red}
            valueStyle={{ color: TRAFFIC.red }}
          />
        </Col>
        <Col xs={12} sm={8} md={6}>
          <StatCard
            title="Fixable Remotely Now"
            value={remoteFixable}
            tooltip="Unhealthy devices reachable over SSH (agent v3.2.5+) that could be fixed from a desk right now — no site visit needed."
            icon={<ApiOutlined />}
            accent={TRAFFIC.green}
            valueStyle={{ color: TRAFFIC.green }}
          />
        </Col>
      </Row>

      {/* Map view — one dot per geolocated device, coloured by health. Outlets get
          coordinates from in-field GeoCapture (the outlet sync carries none), so the
          map shows however many have been captured so far. */}
      <Card
        title={<span>Map view<InfoHint title="Each dot is a device at its outlet's captured GPS location, coloured by health (red/yellow/green). Click a dot for details and a Google Maps link." /></span>}
        style={{ marginTop: 16, marginBottom: 16 }}
        extra={
          <Text type="secondary">
            {geoDevices.length} of {regionDevices.length} located
          </Text>
        }
      >
        <FleetMap devices={regionDevices} />
      </Card>

      <DataTable
        rowKey="deviceId"
        columns={columns}
        dataSource={visibleDevices}
        searchableKeys={["deviceId", "deviceTitle"]}
        searchPlaceholder="Search by device ID or title"
        toolbar={toolbar}
        scrollX={1400}
        onRow={(record) => ({
          onClick: () => openDevice(record.deviceId),
          style: { cursor: "pointer" },
        })}
      />
    </div>
  );
};

export default FleetHealth;
